/**
 * Everything VIEW AIRPLANES' 3D view was *measured* to be — kept apart from the
 * three.js that draws it, so `plane-viewer-stage.test.ts` can check each number
 * against `data/baked` without a GPU. Same split `career/preview-pose.ts` makes
 * against `career/aircraft-preview.ts`.
 *
 * The measurements are all off one artefact, `docs/game/ui-reference/view-airplanes-3d.png`
 * — a live capture of the original's own screen, 640x400, i.e. the logical
 * 320x200 frame at 2x. shading-and-paint.md has already established that every
 * one of its 79 colours is an exact `BARON.PAL` entry (squared distance 0), so
 * a colour read out of it names an index rather than approximating one.
 */

import { BLADE_REGIME_BELOW, DISC_REGIME_ABOVE } from "../flight/propeller-rpm.ts";
import { INITIAL_POSE } from "../career/preview-pose.ts";
import type { PaletteColour } from "../flight/time-of-day.ts";
import type { PlacementOrigin } from "../world/placement-transform.ts";

/** The baked practice range — `VOLUME.004/PTPLANE.WLD` + `RESOURCE.000/PTPLANE.TBL`, 18 placements. */
export const PTPLANE_REGION_PATH = "world/PTPLANE.json";

/**
 * The ground everywhere the aerodrome's own plate is not: `BARON.PAL` **119**
 * `#7d7149`.
 *
 * Measured, not chosen. In the capture the first row below the skyline is
 * exactly this value at *both* edges of the frame — logical y 78 at x 4 and at
 * x 310 — before the plate's own 44 / 127 / 125 take over further down. It is
 * therefore what the horizon is made of, with the aerodrome laid on top of it.
 * It is also the bright end of `world/terrain-palette.ts`'s
 * `GROUND_RELIEF_RAMP`, so it is a colour this port's terrain already paints.
 */
export const AERODROME_BASE_GROUND: PaletteColour = 0x7d7149;

/** `BARON.PAL` 64 — the colour every face of `PTPLANE-13`…`31`, the original's baked ground shadows, carries. */
export const SHADOW_COLOUR: PaletteColour = 0x141414;

/**
 * How high the sun that casts the parked aeroplane's shadow stands, degrees
 * above the horizon.
 *
 * **Measured off the capture's own shadow, not assumed.** Restricted to rows
 * below the skyline — where a ground shadow is the only thing index 64 can be —
 * the original's frame carries **7 360 shadow pixels, 4.29 % of the viewport**,
 * running from logical row 96 down to 133.5 against a skyline at 77.5 and a
 * viewport that ends at 139. Turning those rows back into ground distances
 * through the frame's own 31.7-degree vertical field puts the aeroplane's
 * wheels about 12 m from the lens and the near end of its shadow about 7 m —
 * so the shadow is thrown roughly **5 m toward the viewer** by an airframe
 * about 2.3 m tall, and `atan(2.3 / 5)` is this number.
 *
 * It is a real recovery of the *look* and it should not be over-read: it says
 * the original lit this screen with a low sun throwing a long shadow at the
 * camera, which is the whole reason the shadow is 4 % of its frame and would be
 * a hidden sliver under a noon sun. `flight/time-of-day.ts`'s own daytime sun
 * is within a thousandth of the zenith, so it is not the sun this screen uses
 * and is deliberately not borrowed.
 *
 * The azimuth is left at zero — the shadow falls straight down the default
 * view's own axis, toward the viewer — because the capture's shadow sits
 * square under the aeroplane laterally (columns 55 to 281.5 against an airframe
 * that runs off both edges of the frame) and there is nothing there to recover
 * a bearing from.
 */
export const PARKED_SUN_ELEVATION_DEG = 25;

/**
 * Where the aeroplane stands, in `PlacementOrigin`'s frame — metres on the
 * `.WLD` axes, so `z` is a `.WLD` *y* (`world/placement-transform.ts`).
 *
 * It is the centre of `PTPLANE-09`'s index-125 runway strip, and it is
 * **derived from the baked data, not chosen**: the field plate is placed at
 * `.WLD` (-5632, 1024) = (-214.58, +39.01) m, and the two triangles of that
 * plate carrying `BARON.PAL` 125 span model x 169.2…274.3, y -167.6…154.1,
 * whose centre is (221.74, -6.78). The test recomputes both halves from
 * `data/baked/world/PTPLANE.json` and `data/baked/models/PTPLANE-09.json` and
 * fails if either drifts.
 *
 * The plate's `.WLD` placement fixes only where its *origin* sits; the strip
 * lies 221.7 m along the plate's own x from there, and how far that is in
 * metres is set by `PTPLANE-09`'s `scaleExponent = 2` through
 * `unitsPerMetreForScale` (packages/baker/src/model-bake-extras.ts), which is
 * measured against the original under DOSBox-X in
 * docs/engineering/london-sea-placements.md §8. So the parking spot sits east
 * of the plate's origin, not west of it, and both halves of that are data.
 *
 * ## Why that strip and not the field or the apron
 *
 * The plate carries three surfaces — an 861 x 580 m field in index 44, a
 * 280 x 520 m apron in 127, and this 105 x 322 m strip in **125**. In the
 * original's capture index 125 is **30 % of every pixel in the frame**, far
 * more than either of the others, and it is the colour of the ground the
 * aeroplane's wheels are on. It is the runway, and the aeroplane is parked on
 * it. That also explains the capture's own columns: the strip is 105 m wide,
 * so at `PARKED_EYE_M` the bottom of a 30-degree frame meets the ground about
 * 7 m ahead where the frame is under 10 m across — the strip fills the near
 * picture edge to edge — and only further out does the frame widen past it and
 * let 127 and 44 appear at the sides, which is the order those columns step
 * through.
 *
 * The independent check is what else lands in shot from here: `PTPLANE-08`,
 * the flag on its pole, is 223 m away within three degrees of straight ahead
 * down the strip; `PTPLANE-05`, the shed, is 264 m out and 30° to the left,
 * comfortably inside a 70°-wide frame; the six `PTPLANE-12` copses are 394 m
 * and further, spread behind and to the sides. That is the capture's
 * arrangement — strip underfoot, one silhouette near the centre, one off to a
 * side, trees beyond — and none of it was positioned by this port.
 *
 * What the capture does *not* pin is which of the two silhouettes is the
 * centred one: at 223 m against 264 m either would read as a hut-sized shape,
 * and nothing in the frame distinguishes them. `plane-viewer-stage.test.ts`
 * therefore asserts the arrangement and not the identity.
 */
export const RUNWAY_ORIGIN: PlacementOrigin = { x: 7.16, z: 32.23 };

/**
 * Eye height on the field, metres.
 *
 * Read off the capture rather than assumed. A level camera puts everything at
 * its own eye height on the horizon line, and in the capture the Eindecker's
 * wing crosses *above* that line — so the eye is below an E.III's 2.4 m wing
 * and above its wheels. A standing man's eye is the only reading that fits, and
 * it is what the picture shows: you are on the aerodrome looking at the
 * aeroplane, not hovering over it. It is also why this screen's camera has no
 * pitch where the career turntable has -0.32 rad — see `parked-preview.ts`.
 */
export const PARKED_EYE_M = 1.7;

/**
 * Turns the stage under the view arrows so that the pose they start from is the
 * view the capture frames — looking down the runway strip at the shed, with the
 * flag and the copse to either side.
 *
 * Expressed against `INITIAL_POSE` rather than written as a number: the camera
 * sits at `(sin yaw, ·, cos yaw)` looking back at the aeroplane, so a total yaw
 * of 180 degrees is the one that looks along +Z, which is the direction of
 * decreasing `.WLD` y — the way the shed lies from the strip.
 */
export const PARKED_YAW_OFFSET_DEG = 180 - INITIAL_POSE.yawDeg;

/**
 * Which way the aeroplane itself is pointing on the strip, degrees about world
 * Y — negative turns its nose towards the left of the default frame.
 *
 * **The aeroplane is turned, not the camera, and the original is why.** In the
 * capture the Eindecker is at a strong three-quarter to the lens, nose to the
 * left, while the shed sits close to the middle of the frame behind it. Those
 * two facts cannot both hold with the aeroplane square to the runway: turning
 * the *camera* far enough off the strip to get a three-quarter view swings the
 * shed straight out of a 30-degree frame (it is only about 31 degrees wide),
 * and the capture plainly still has it. So the aeroplane is parked across the
 * strip, which is also just what a parked aeroplane does — nothing in the scene
 * it is standing on is aligned with it either.
 *
 * The consequence worth knowing: this is a **pose**, not a measurement. The
 * strip's own bearing and everything on the horizon come from `PTPLANE.WLD`;
 * the angle the aeroplane sits at on it is chosen to frame the aeroplane the
 * way the original's own frame does, and the view arrows move the camera off it
 * from there.
 */
export const PARKED_HEADING_DEG = -40;

/**
 * How fast the parked aeroplane's propeller is drawn as turning, as a fraction
 * of full rate — the one number `flight/propeller-discs.ts` needs from a scene
 * with no `SimState` in it.
 *
 * **Chosen from the original's picture, not from an idle-RPM model**, because
 * the original draws a propeller here and it can simply be read. At the
 * Eindecker's nose the capture has a solid disc of `BARON.PAL` **75**
 * `#717171` with solid black blade silhouettes across it — and neither of those
 * comes from the aeroplane: `FOKE3.json` contains no index 75 anywhere in any
 * LOD, and its own nose greys are 68, 69 and 80. So the viewer is drawing a
 * propeller, and drawing it **in both regimes at once**: a disc *and* blades.
 *
 * That is worth stating plainly because AD-012 opens with "the original draws
 * no propeller in any view". That finding was made against `RB.EXE`'s flight
 * views and it stands there. This screen is `PS.EXE`'s, and this screen does.
 *
 * `flight/propeller-rpm.ts` crossfades from blades to disc between
 * `BLADE_REGIME_BELOW` and `DISC_REGIME_ABOVE`, so the rate that shows both
 * equally is the midpoint of that band — which is what this is, derived from
 * those two constants rather than written as a literal so it cannot drift away
 * from them. Note what it is *not*: an idling Clerget is 650 of 1250 rev/min,
 * i.e. a fraction of 0.52, which is past `DISC_REGIME_ABOVE` and would draw the
 * shimmer disc alone with no blades in it. The original's own frame is the
 * better authority for what this screen looks like than a rotary's idle speed
 * the original was not simulating.
 */
export const PARKED_RPM_FRACTION = BLADE_REGIME_BELOW + (DISC_REGIME_ABOVE - BLADE_REGIME_BELOW) / 2;
